/**
 * SIMS-8687 AC2 (PRD FR-21) — action-code normalisation and the uniqueness comparison.
 *
 * FR-21: "An action's code cannot change after creation; codes are unique, uppercase, no spaces."
 * AC2: "when I enter a code in lower case, with spaces, or matching an existing action, then it is
 * upper-cased and stripped of spaces, and a duplicate is refused with a message."
 *
 * TWO FUNCTIONS, BECAUSE THE SOURCES CONFLICT ON ONE POINT AND BOTH READINGS MATTER.
 *
 * `normaliseActionCode` is what gets STORED, exactly as AC2 words it: trimmed, spaces stripped,
 * upper-cased. `ACTION ONE` becomes `ACTIONONE`; an underscore the user typed is theirs to keep.
 *
 * `actionCodeKey` is what uniqueness COMPARES, and it folds separators too. That is needed because
 * TestRail C17715 requires BOTH `'action one'` AND `'Action_One'` to collide with an existing
 * `ACTION_ONE` — and under AC2's stored form the first one does not (`ACTIONONE` != `ACTION_ONE`).
 * Rather than pick one source over the other, the stored value follows AC2 and the comparison
 * follows the case: `ACTION_ONE`, `action one` and `Action_One` all key to `ACTIONONE`, so the
 * same permission cannot enter twice in different spellings, which is the point C17715 is making
 * ("Normalise-then-compare, or the same code enters twice in different spellings").
 *
 * The residual question is for the PO and is recorded on the case: whether a space SHOULD become
 * an underscore rather than vanish. If it should, `normaliseActionCode` changes and `actionCodeKey`
 * does not — the comparison is already insensitive to it.
 */

/** The stored form: trimmed, all whitespace stripped, upper-cased (AC2 / FR-21). */
export const normaliseActionCode = (raw: string): string =>
	raw.replace(/\s+/g, "").toUpperCase();

/**
 * The uniqueness key: the stored form with separators folded away, so two codes that differ only
 * by case or by where the separators fall are the same code. See the note above.
 */
export const actionCodeKey = (raw: string): string =>
	normaliseActionCode(raw).replace(/[_\-.]/g, "");

/**
 * The action already using `candidate`, or undefined.
 *
 * `excludeId` skips the record being edited — without it, saving an existing action would find
 * itself and report its own code as a duplicate. (The code is immutable on edit per AC3, so this
 * is belt and braces rather than a live path, but a validator that reports a false conflict is
 * worse than one that never runs.)
 */
export const findActionCodeConflict = <T extends { _id: string; code: string }>(
	actions: T[],
	candidate: string,
	excludeId?: string,
): T | undefined => {
	const key = actionCodeKey(candidate);
	if (key === "") {
		return undefined;
	}
	return actions.find(
		(action) => action._id !== excludeId && actionCodeKey(action.code) === key,
	);
};
